import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';

import { BuildingTabs } from '@klp/bygg/classes';
import { ByggRoutes } from '@klp/bygg/config';
import { Client } from '@klp/client';
import {
    useSelectClientMutation,
    useResetClientMutation,
} from '@klp/client/components/Select/rtkApi';
import { Conditional } from '@klp/conditional';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionButton, Block, Heading } from '@klp/design';
import { Loader } from '@klp/design/Loader';
import { Table, TableColumnDef } from '@klp/design/Table';
import { ErrorNotification } from '@klp/errorNotification';
import { Search, filterSearchResult } from '@klp/search';

import type { ByggKunde } from '../classes';
import { useGetByggKunderQuery } from '../rtkApi';

interface IOwnProps {
    byggNummer: string;
}

export const ByggKunderList: React.FC<IOwnProps> = ({ byggNummer }) => {
    const [search, setSearch] = useState('');
    const history = useHistory();

    // RTK Query
    const { data: responseData, isLoading: byggKunderLoading } =
        useGetByggKunderQuery({ byggNummer }, { skip: !byggNummer });

    const byggKunder = useMemo(
        () => responseData?.byggKunder || [],
        [responseData?.byggKunder],
    );

    const { selectedClient } = useAppParams();

    const [selectClient] = useSelectClientMutation();
    const [resetClient] = useResetClientMutation();

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const getTableColumns = () => [
        new TableColumnDef({
            key: 'kundeNummer',
            sortable: true,
            translation: 'clientSelect.clientNumber',
            align: 'left',
        }),
        new TableColumnDef({
            key: 'kundeNavn',
            sortable: true,
            translation: 'clientSelect.clientName',
            align: 'left',
        }),
        new TableColumnDef({
            key: 'sistAktivDato',
            sortable: true,
            translation: 'clientSelect.clientLastActiveInML',
            align: 'left',
            type: 'date',
        }),
        new TableColumnDef({
            key: 'velkomstmeldingSendtDato',
            sortable: true,
            translation: 'clientSelect.welcomeMessageSent',
            align: 'left',
            type: 'date',
        }),
    ];

    const navigateToBuildingOverview = () =>
        history.push(ByggRoutes.single(byggNummer, BuildingTabs.overview));

    const handleRowSelect = (byggKunde: ByggKunde) => {
        selectClient(Client.fromByggKunde(byggKunde));
        navigateToBuildingOverview();
    };

    const handleSelectClientReset = () => {
        resetClient();
    };

    const getActiveClients = (
        byggKundeList: Array<ByggKunde>,
    ): Array<ByggKunde> =>
        byggKundeList.filter((byggKunde) => byggKunde.aktive > 0);

    const getFutureClients = (
        byggKundeList: Array<ByggKunde>,
    ): Array<ByggKunde> =>
        byggKundeList.filter(
            (byggKunde) => byggKunde.fremtidige > 0 && byggKunde.aktive === 0,
        );

    const getInactiveClients = (
        byggKundeList: Array<ByggKunde>,
    ): Array<ByggKunde> =>
        byggKundeList.filter(
            (byggKunde) =>
                byggKunde.inaktive >= 0 &&
                byggKunde.aktive === 0 &&
                byggKunde.fremtidige === 0,
        );

    const safeByggKunder = byggKunder || [];
    const searchResult = filterSearchResult<ByggKunde>(safeByggKunder, search, [
        'kundeNummer',
        'kundeNavn',
    ]);

    const activeClients = getActiveClients(searchResult);
    const inactiveClients = getInactiveClients(searchResult);
    const futureClients = getFutureClients(searchResult);

    return (
        <Loader isLoading={byggKunderLoading} data-testid="ByggKunderList">
            <Conditional
                condition={!byggKunderLoading && safeByggKunder.length > 0}
                errorComponent={
                    <ErrorNotification
                        title="bygg.errorNoClients.title"
                        backTarget={ByggRoutes.single(
                            byggNummer,
                            BuildingTabs.overview,
                        )}
                        backTitle="general.goBack"
                        data-testid="ByggKunderList"
                    />
                }
            >
                <Grid container={true}>
                    {selectedClient && (
                        <Block marginBottom="bulky">
                            <ActionButton
                                actionType="cancel"
                                translation="clientSelect.selectedClient"
                                translationOptions={{
                                    customerName: selectedClient.kundeName,
                                }}
                                onClick={handleSelectClientReset}
                                clickHandler={handleSelectClientReset}
                            />
                        </Block>
                    )}
                    <Grid item={true} xs={12}>
                        <Grid container={true}>
                            <Grid item={true} xs={12} sm={6} md={4}>
                                <Search
                                    id="ClientSearch"
                                    searchHandler={handleSearch}
                                    searchOnType={true}
                                    placeholder="clientSelect.searchClient"
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item={true} xs={12}>
                        <Heading
                            klpHeadingLevel="THREE"
                            marginTop="bulky"
                            marginBottom="tiny"
                            translation="bygg.activeClients"
                        />
                        <Table<ByggKunde, unknown>
                            id="ByggKunderListActive"
                            emptyTableMessage="clientSelect.notFound"
                            data={activeClients}
                            columns={getTableColumns()}
                            sortBy={getTableColumns()[1]}
                            sortByDirection="asc"
                            highlightPhrase={search}
                            onRowSelect={handleRowSelect}
                            data-testid="ByggKunderList"
                        />
                    </Grid>

                    <Grid item={true} xs={12}>
                        <Heading
                            klpHeadingLevel="THREE"
                            marginTop="bulky"
                            marginBottom="tiny"
                            translation="bygg.futureClients"
                        />
                        <Table<ByggKunde, unknown>
                            id="ByggKunderListFuture"
                            emptyTableMessage="clientSelect.notFound"
                            data={futureClients}
                            columns={getTableColumns()}
                            sortBy={getTableColumns()[1]}
                            sortByDirection="asc"
                            highlightPhrase={search}
                            onRowSelect={handleRowSelect}
                        />
                    </Grid>

                    <Grid item={true} xs={12}>
                        <Heading
                            klpHeadingLevel="THREE"
                            marginTop="bulky"
                            marginBottom="tiny"
                            translation="bygg.inactiveClients"
                        />
                        <Table<ByggKunde, unknown>
                            id="ByggKunderListInactive"
                            emptyTableMessage="clientSelect.notFound"
                            data={inactiveClients}
                            columns={getTableColumns()}
                            sortBy={getTableColumns()[1]}
                            sortByDirection="asc"
                            highlightPhrase={search}
                            onRowSelect={handleRowSelect}
                            initialRowsPerPage={10}
                        />
                    </Grid>
                </Grid>
            </Conditional>
        </Loader>
    );
};

ByggKunderList.displayName = 'ByggKunderList';
